import { Transition } from '@headlessui/react'
import { Message, Spinner } from '@sbab-ui/components'
import { AppState } from '../appState'
import {
  getCalculationExample,
  getEssentialDerivedState,
  getMonthlyAmortization,
  getMonthlyInterestCost,
  getMonthlyInterestCostWithTaxDeduction,
  getMonthlyPersonalLoanAmortization,
} from '../lib/calculationUtils'
import { formatValueForDisplay } from '../lib/formattingUtils'
import QueryLinkButton, { ButtonConfig } from './QueryLinkButton'
import SlideOver from './SlideOver'
import Table from './Table'
import TableRow from './TableRow'
import { TypographyH3, TypographyP } from './typographyComponents'

interface Props {
  appState: AppState
}

const defaultButtonConfig: ButtonConfig[] = [
  {
    config: 'calculateLoanAmount',
    text: 'Se hur mycket du kan låna',
    variant: 'primary',
    explanationText: {
      subheading: 'Vill du veta hur mycket du kan låna?',
      paragraph:
        'Räkna ut hur mycket du får låna och se hur mycket du maximalt kan köpa en bostad för.',
    },
  },
  {
    config: 'preApproval',
    text: 'Ansök om lånelöfte',
    variant: 'secondary',
    explanationText: {
      subheading: 'Skaffa lånelöfte innan visningen!',
      paragraph:
        'Lånelöftet är generellt och gäller inte för en specifik bostad. Och självklart är det inte bindande!',
    },
  },
]

// The window.pcaConfig?.buttonConfig is feature toggleable
const buttonConfig = window.pcaConfig?.buttonConfig || defaultButtonConfig

function ErrorMessages({
  textMessages,
}: Readonly<{
  textMessages: { text: string; id: number }[]
}>) {
  return (
    <div className="relative">
      <div className="w-fullz-10 absolute left-0 right-0 mx-auto  flex flex-col justify-center">
        {textMessages.map((message) => (
          <Message tone="critical" className="mt-4" key={message.id} data-cy="result-error-msg">
            {message.text}
          </Message>
        ))}
      </div>
    </div>
  )
}

function Result({ appState }: Readonly<Props>) {
  const {
    downPayment,
    propertyValue,
    mortgageRateIndex,
    listInterests,
    monthlyFee,
    yearlyOperatingCost,
    interestDeviation,
    personalLoanAmount,
    userIsChangingMortgageValues,
    personalLoanLengthInYears,
    personalLoanExampleRate,
    personalLoanIsAdded,
    hasCalculationError,
    errorMessage,
  } = appState

  // DERIVED STATE:
  const {
    effectivePersonalLoanAmount,
    showPersonalLoan,
    totalIsEnoughForPurchase,
    downPaymentPercentage,
    personalLoanPercentage,
  } = getEssentialDerivedState(downPayment, propertyValue, personalLoanIsAdded, personalLoanAmount)
  const mortgageRate = +listInterests[mortgageRateIndex].interestRate

  // MORTGAGE:
  const loanAmount = propertyValue - downPayment - effectivePersonalLoanAmount
  const loanToValuePercentage =
    effectivePersonalLoanAmount === 0
      ? 100 - downPaymentPercentage
      : 100 - (personalLoanPercentage + downPaymentPercentage)
  const monthlyAmorization = getMonthlyAmortization(propertyValue, loanAmount)
  const monthlyOperatingCost = yearlyOperatingCost / 12
  const adjustedInterestRate = mortgageRate + interestDeviation

  const monthlyInterestWithTaxDeduction = getMonthlyInterestCostWithTaxDeduction(
    adjustedInterestRate,
    loanAmount,
  )

  const monthlyInterest = getMonthlyInterestCost(adjustedInterestRate, loanAmount)

  // PERSONAL LOAN:
  const monthlyPersonalLoanAmortization = getMonthlyPersonalLoanAmortization(
    effectivePersonalLoanAmount,
    personalLoanLengthInYears,
  )

  const monthlyPersonalLoanInterestCost = getMonthlyInterestCost(
    personalLoanExampleRate,
    effectivePersonalLoanAmount,
  )

  // TOTALS:
  const monthlyCostWithTaxDeduction = Math.round(
    monthlyAmorization +
    monthlyInterestWithTaxDeduction +
    monthlyFee +
    monthlyOperatingCost +
    monthlyPersonalLoanAmortization +
    monthlyPersonalLoanInterestCost,
  )

  const monthlyCost = Math.round(
    monthlyAmorization +
    monthlyInterest +
    monthlyFee +
    monthlyOperatingCost +
    monthlyPersonalLoanAmortization +
    monthlyPersonalLoanInterestCost,
  )

  // Calculation Examples
  const { totalLoanCost, remainingAmount } = getCalculationExample(
    monthlyAmorization,
    loanAmount,
    adjustedInterestRate,
    40,
  )

  const personalLoanCalculationExample = getCalculationExample(
    monthlyPersonalLoanAmortization,
    effectivePersonalLoanAmount,
    personalLoanExampleRate,
    personalLoanLengthInYears,
  )

  // BLUR OR NOT:
  const blur = !totalIsEnoughForPurchase || userIsChangingMortgageValues || hasCalculationError

  const hasError = !totalIsEnoughForPurchase || hasCalculationError
  function getErrorMessages() {
    let counter = 0
    const messageArray = []
    !totalIsEnoughForPurchase &&
      messageArray.push({
        text: 'Kontantinsatsen behöver vara minst 15\u00A0% av slutpriset. Du kan läsa om och lägga till ett privatlån i denna kalkyl.',
        id: counter++,
      })
    hasCalculationError && messageArray.push({ text: errorMessage, id: counter++ })
    return messageArray
  }
  return (
    <section
      className={`bg-surface-7 px-4 py-8 transition-height duration-1000 ease-in-out sm:px-6 ${blur ? 'text-neutral-95' : 'text-black'
        }`}
    >
      {userIsChangingMortgageValues ? (
        <div className="relative">
          <div className="w-fullz-10 absolute left-0 right-0 mx-auto  flex justify-center">
            <Spinner />
          </div>
        </div>
      ) : null}
      {hasError ? <ErrorMessages textMessages={getErrorMessages()} /> : null}
      <SlideOver
        buttonText="Boendekostnad efter skatteavdrag för bolån"
        title="Skatteavdrag"
        type="h2"
        data-cy="slideover-tax-deduction"
      >
        <TypographyP type="lead">
          När du har ett bolån betalar du en ränta för lånet. Räntan som du betalar har du rätt att
          göra avdrag för i samband med din deklaration. Oftast kallas det för skatteavdrag eller
          ränteavdrag. Om privatlån har lagts till i kalkylen räknar vi utan eventuella skatteavdrag 
          för det privatlånet. Mer information om skatteavdrag hittar du hos Skatteverket. 
        </TypographyP>
      </SlideOver>
      <div data-cs-mask>
        <TypographyH3
          className="!text-4xl font-medium min-[375px]:!text-5xl"
          data-cy="total-cost-per-month">
          {formatValueForDisplay(monthlyCostWithTaxDeduction)} kr/mån
        </TypographyH3>
        <TypographyP type="base" className="!pb-6 !pt-4">
          Före skatteavdrag {formatValueForDisplay(monthlyCost)} kr/mån
        </TypographyP>
      </div>
      <div className="h-2" />
      <div>
        {buttonConfig.map((button, index) => (
          <QueryLinkButton
            variant={button.variant}
            config={button.config}
            text={button.text}
            explanationText={button.explanationText}
            appState={appState}
            key={index}
          />
        ))}
      </div>
      <div className="h-0 sm:h-2" />
      <Table header="Bolån">
        <TableRow value={`${formatValueForDisplay(loanAmount)} kr`}>
          <TypographyP>Lånebelopp</TypographyP>
        </TableRow>
        <TableRow
          value={`${loanToValuePercentage.toString().replace('.', ',')}\u00A0%`}
          data-cy="result-table-row-loan-to-value"
        >
          <SlideOver type="iconRight" title="Belåningsgrad">
            <TypographyP type="lead">
              Med belåningsgrad menas hur mycket lån du har på din bostad jämfört med
              marknadsvärdet. Köper du ett hus för 3 000 000 kronor innebär 65&nbsp;% belåning att
              lånet är på 1 950 000 kronor.
            </TypographyP>
            <TypographyP type="lead">
              Du kan räkna ut belåningsgraden genom att ta lånebeloppet delat med bostadens värde.
            </TypographyP>
          </SlideOver>{' '}
        </TableRow>
        <TableRow value={`fr. ${formatValueForDisplay(monthlyAmorization)} kr/mån`}>
          <SlideOver type="iconRight" title="Amortering">
            <TypographyP type="lead">
              Amortering är det belopp du betalar av på ditt lån vid varje betalningstillfälle.
              Beroende på hur mycket du lånar i förhållande till din bostads värde och din inkomst
              kan du behöva amortera 1-3&nbsp;% av det totala lånebeloppet per år.
            </TypographyP>
            <TypographyP type="lead">
              Bolån mellan 50–70&nbsp;% av bostadens värde ska amorteras med ett belopp motsvarande
              1&nbsp;% av det totala lånebeloppet per år.
            </TypographyP>
            <TypographyP type="lead">
              Bolån över 70&nbsp;% av bostadens värde ska amorteras med ett belopp motsvarande
              2&nbsp;% av det totala lånebeloppet per år.
            </TypographyP>
            <TypographyP type="lead">
              Om du lånar mer än 4,5 gånger din årsinkomst före skatt ska du även amortera 1&nbsp;%
              av det totala lånebeloppet per år.
            </TypographyP>
          </SlideOver>
        </TableRow>
        <TableRow value={`${adjustedInterestRate.toFixed(2).replace('.', ',')}\u00A0%`}>
          <SlideOver type="iconRight" title="Ränta">
            <TypographyP type="lead">
              När du lånar hos oss sätts din ränta utifrån hur mycket du lånar i förhållande till
              värdet på din bostad. Det avgör vilket avdrag du får på listräntan. Våra avdrag är
              inte tidsbestämda rabatter som försvinner efter en bestämd period, vilket är vanligt
              hos många andra banker.
            </TypographyP>
          </SlideOver>
        </TableRow>
        <TableRow value={`${formatValueForDisplay(monthlyInterestWithTaxDeduction)} kr/mån`}>
          <SlideOver type="iconRight" title="Räntekostnad">
            <TypographyP type="lead">Räntekostnad efter skatteavdrag.</TypographyP>
            <TypographyP type="lead">
              När du har ett bolån betalar du en ränta för lånet. Räntan som du betalar har du rätt
              att göra avdrag för i samband med din deklaration. Oftast kallas det för skatteavdrag
              eller ränteavdrag. Mer information om skatteavdrag hittar du hos Skatteverket.
            </TypographyP>
          </SlideOver>
        </TableRow>
      </Table>
      <Table header="Boende">
        <TableRow value={`${formatValueForDisplay(monthlyFee)} kr/mån`}>
          <TypographyP>Avgift</TypographyP>
        </TableRow>
        <TableRow value={`${formatValueForDisplay(Math.round(monthlyOperatingCost))} kr/mån`}>
          <TypographyP>Driftkostnad</TypographyP>
        </TableRow>
      </Table>
      <Transition
        show={personalLoanIsAdded && showPersonalLoan}
        enter="transition-all duration-500"
        enterFrom="opacity-0 max-h-[0vh]"
        enterTo="opacity-100 max-h-[100vh]"
        leave="transition-all duration-500"
        leaveFrom="opacity-100 max-h-[100vh]"
        leaveTo="opacity-0 max-h-[0vh]"
      >
        <Table header="Privatlån" data-cy="personal-loan-table">
          <TableRow value={`${formatValueForDisplay(personalLoanAmount)} kr`}>Lånebelopp</TableRow>
          <TableRow value={`${formatValueForDisplay(monthlyPersonalLoanAmortization)} kr/mån`}>
            <SlideOver type="iconRight" title="Amortering">
              <TypographyP type="lead">
                Privatlånet amorteras enligt rak amortering. Det innebär att du amorterar lika stor
                summa varje månad, vilket gör att lånet blir mindre varje månad och därmed också
                räntekostnaden för det återstående lånet.
              </TypographyP>
              <TypographyP type="lead">
                Ju kortare avbetalningstid du väljer desto billigare blir den totala kostnaden för
                ditt lån. Kortare avbetalningstid innebär samtidigt att den totala månadskostnaden
                ökar och det viktiga är att du klarar av dina månatliga betalningar.
              </TypographyP>
            </SlideOver>
          </TableRow>
          <TableRow value={`${personalLoanExampleRate.toFixed(2).replace('.', ',')}\u00A0%`}>
            <SlideOver type="iconRight" title="Exempelränta">
              <TypographyP type="lead">
                Vi har räknat med en exempelränta. Din ränta sätts när ditt privatlån betalas ut och
                baseras på dina specifika förutsättningar och räntenivån just då.
              </TypographyP>
            </SlideOver>
          </TableRow>
          <TableRow value={`${formatValueForDisplay(monthlyPersonalLoanInterestCost)} kr/mån`}>
            <SlideOver type="iconRight" title="Räntekostnad">
              <TypographyP type="lead">
              Räntekostnaden för privatlån visas utan eventuella avdrag för skatt.
              </TypographyP>
            </SlideOver>
          </TableRow>
          <TableRow value={`${personalLoanLengthInYears} år`}>Återbetalningstid</TableRow>
        </Table>
      </Transition>
      <SlideOver title="Mer om hur vi räknat">
        <TypographyP type="lead">
          Månadskostnaden i kalkylen avser första månadsbetalningen. Vi har ingen uppläggningsavgift
          och ingen aviavgift när du betalar med autogiro, e-faktura eller SBABgiro. Om du betalar
          med pappersfaktura utgår en avgift på 25 kr per betalning.
        </TypographyP>
        <TypographyP type="lead">
          Tänk på att ett bolån hos SBAB förutsätter panträtt i fast egendom, tomträtt, bostadsrätt
          eller andel i bostadsförening.
        </TypographyP>
        <TypographyP type="lead">
          Löptiden för nya bolån hos SBAB är 40 år. Löptiden är den tid du ska betala tillbaka lånet
          på. Det betyder att om du har en låg amortering så kan du ha kvar en restskuld att betala
          den dagen då lånets löptid går ut.
        </TypographyP>
        <TypographyP type="lead">
          Tänk på att ett privatlån hos SBAB inte förutsätter panträtt i fast egendom, tomträtt,
          bostadsrätt eller andel i bostadsförening.
        </TypographyP>
        <TypographyP type="lead">
          Om du har inkomst i utländsk valuta kan valutakursförändringar komma att påverka de belopp
          som ska betalas.
        </TypographyP>
        <TypographyH3>Räkneexempel - Bolån</TypographyH3>
        <div data-cs-mask>
          <TypographyP type="lead">
            Beräknat på ett bolån på <strong>{formatValueForDisplay(loanAmount)} kr</strong> med en
            löptid på <strong>40 år</strong>, med en amortering på{' '}
            <strong>{formatValueForDisplay(monthlyAmorization)} kr</strong> i månaden och en ränta på{' '}
            <strong>{formatValueForDisplay(adjustedInterestRate)}&nbsp;%</strong> blir det totala
            beloppet att betala <strong>{formatValueForDisplay(totalLoanCost)} kr</strong>.{' '}
            {remainingAmount > 0 ? (
              <span>
                Om du inte amorterar mer under den här tiden kommer det, när löptiden går ut, att
                finnas kvar en restskuld på{' '}
                <strong>{formatValueForDisplay(remainingAmount)} kr.</strong>
              </span>
            ) : null}
          </TypographyP>
          {personalLoanIsAdded ? (
            <>
              <TypographyH3>Räkneexempel - Privatlån</TypographyH3>
              <TypographyP type="lead">
                Beräknat på att lånet på{' '}
                <strong>{formatValueForDisplay(personalLoanAmount)} kr</strong> har en en
                avbetalningstid på <strong>{personalLoanLengthInYears} år</strong> och en amortering
                på <strong>{formatValueForDisplay(monthlyPersonalLoanAmortization)} kr</strong> i
                månaden och en ränta på{' '}
                <strong>{personalLoanExampleRate.toFixed(2).replace('.', ',')}&nbsp;%</strong> blir
                det totala beloppet att betala för privatlånet{' '}
                <strong>
                  {formatValueForDisplay(personalLoanCalculationExample.totalLoanCost)} kr
                </strong>
                .
              </TypographyP>
            </>
          ) : null}
        </div>
      </SlideOver>
    </section>
  )
}

export default Result
